// Conciliación Bancaria v2 — daily anti-fraud control.
//
// One multi-account movements ledger. Bank lines are parents; TPV/Clip detail are
// sub-movements. We reconcile subs/leaves 1:1 against the real payments the sellers
// entered; parents auto-complete when their subs tie out. Matching is multi-pass
// (rules → unique keys → transfer pairing). Exceptions are the daily work.
// Humans verify everything for now.
import React, { useState, useEffect, useCallback, useRef } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page, Card, DropZone, BlockStack, InlineGrid, InlineStack, Text, Badge,
  Button, IndexTable, Spinner, Modal, Select, TextField, Box, Tooltip,
  ButtonGroup, EmptyState, ProgressBar, Checkbox,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatDate } from "../../utility/funcs";
import { parseMulticash, isMulticash } from "../../utility/multicash";
import { parseMT940 } from "../../utility/mt940";
import { parseAfirmeTpv, isAfirmeTpv } from "../../utility/afirmeTpv";
import {
  categorize, dedupeHash, assembleTpvSubs, matchPayment, ruleFor, pairTransfers,
  computeExceptions, absDayDiff, dayDiff, CATEGORY, CATEGORY_LABEL, INCOME_LIKE,
} from "../../utility/bankReconciliation";
import { getClipSettlements, getClipSettlementDetail, clipSettlementToSubs } from "../../utility/clip";

const EPS = 0.01;
const todayISO = () => new Date().toISOString().substring(0, 10);
const addDaysISO = (iso, n) => {
  const dt = new Date(`${iso}T00:00:00Z`); dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().substring(0, 10);
};
const txnDate = (t) => (t.payment_date || t.created_at || "").substring(0, 10);
const thisMonth = () => todayISO().substring(0, 7);
const monthRange = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const start = `${ym}-01`;
  const end = new Date(Date.UTC(y, m, 0)).toISOString().substring(0, 10); // last day
  return { start, end };
};
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("es-MX", { month: "long", year: "numeric", timeZone: "UTC" });
};
const recentMonths = (n) => {
  const out = []; const now = new Date();
  for (let i = 0; i < n; i++) { const dt = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1)); out.push(dt.toISOString().substring(0, 7)); }
  return out;
};

// Enumerate every subset of `items` whose amounts sum to `target` (± a centavo).
// DFS over amounts sorted desc with two prunes — overshoot (amount > remaining)
// and can't-reach (suffix sum < remaining) — plus hard result/node caps so a
// pathological set (many equal amounts) can't hang the tab. `capped` flags when
// a limit was hit, so the UI can say "showing first N".
function subsetSumCombos(items, target, { maxResults = 40, maxNodes = 200000 } = {}) {
  const arr = [...items].sort((a, b) => b.amount - a.amount);
  const n = arr.length;
  const suffix = new Array(n + 1).fill(0);
  for (let i = n - 1; i >= 0; i--) suffix[i] = suffix[i + 1] + arr[i].amount;
  const eps = 0.01;
  const results = [];
  const chosen = [];
  let nodes = 0, capped = false;
  const dfs = (i, remaining) => {
    if (results.length >= maxResults) { capped = true; return; }
    if (nodes++ > maxNodes) { capped = true; return; }
    if (Math.abs(remaining) < eps && chosen.length) { results.push([...chosen]); return; }
    if (i >= n || remaining < -eps || suffix[i] < remaining - eps) return;
    if (arr[i].amount <= remaining + eps) {           // include arr[i]
      chosen.push(arr[i]);
      dfs(i + 1, remaining - arr[i].amount);
      chosen.pop();
    }
    dfs(i + 1, remaining);                              // exclude arr[i]
  };
  dfs(0, target);
  return { results, capped };
}

// Normalize either bank parser to a common movement shape.
function normalizeBank(text) {
  if (isMulticash(text)) {
    const p = parseMulticash(text);
    return {
      format: "MultiCash",
      movements: p.movements.map((m) => ({
        date: m.date, amount: m.amount, credit: m.credit,
        shortConcept: m.shortConcept, description: m.description, concepto: m.concepto,
        payerRfc: m.payerRfc, payerName: m.payerName, payerClabe: m.payerClabe,
        claveRastreo: m.claveRastreo, reference: m.reference, direction: m.direction,
      })),
    };
  }
  const p = parseMT940(text);
  return {
    format: "MT940",
    movements: p.movements.map((m) => ({
      date: m.date, amount: m.amount, credit: m.credit,
      shortConcept: m.code, description: m.description, concepto: m.description,
      payerRfc: m.payerRfc, payerName: m.payerName, payerClabe: m.counterpartyClabe,
      claveRastreo: "", reference: m.reference, direction: m.credit ? "RECIBIDO" : "ENVIADO",
    })),
  };
}

function ConciliacionBancaria() {
  const { session } = UserAuth();
  const userId = session?.user?.id || null;

  const [accounts, setAccounts] = useState([]);
  const [filterAccount, setFilterAccount] = useState("all");
  const [hideReconciled, setHideReconciled] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [month, setMonth] = useState(thisMonth());
  const [rules, setRules] = useState([]);
  const [movements, setMovements] = useState([]);
  const [exceptions, setExceptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  // Caches so a single verify can update locally without re-querying everything.
  const movementsRef = useRef([]);
  const exDataRef = useRef({ unusedIncome: [], unusedOutflow: [] });
  useEffect(() => { movementsRef.current = movements; }, [movements]);

  const [importAccount, setImportAccount] = useState("");
  const [bankFile, setBankFile] = useState(null);
  const [tpvFile, setTpvFile] = useState(null);
  const [progress, setProgress] = useState(null); // { pct, label } while importing
  const [regRow, setRegRow] = useState(null);
  const [regForm, setRegForm] = useState({ reservation: "" });
  // Fix a mis-categorized movement.
  const [catRow, setCatRow] = useState(null);
  const [catValue, setCatValue] = useState("");
  // Manual "tie this bank line to already-recorded payment(s)" modal.
  const [matchRow, setMatchRow] = useState(null);
  const [matchCands, setMatchCands] = useState([]);
  const [matchSel, setMatchSel] = useState(new Set());
  const [matchGroup, setMatchGroup] = useState("none"); // none | reservation | customer
  const [matchCombos, setMatchCombos] = useState(null); // null=not run, []=none found, [...]=combinations

  // Detect which account a statement belongs to. Use the statement's OWN account
  // header (MT940 :25:) — NOT any CLABE in the body, since a statement references
  // counterparty CLABEs (e.g. a transfer into another of our accounts) that would
  // mis-detect. For MultiCash (no :25:) fall back to a raw scan.
  const detectAccount = (text) => {
    // MT940 only: the :25: account tag sits at the start of a line. Anchor to
    // line-start (and skip entirely for MultiCash) so SPEI timestamps like
    // "HORA:13:25:03" in a UMSATS file don't get misread as the account header.
    const hdr = isMulticash(text) ? null : (text.match(/^:25:\s*([A-Z0-9/]+)/m) || [])[1];
    if (hdr) {
      return accounts.find((a) => (a.statement_keys || []).some(
        (k) => k && (hdr.includes(k) || k.includes(hdr)))) || null;
    }
    return accounts.find((a) => (a.statement_keys || []).some((k) => k && text.includes(k))) || null;
  };

  // ---------- initial load ----------
  useEffect(() => {
    (async () => {
      const [{ data: accs }, { data: rls }] = await Promise.all([
        supabase.from("transaction_accounts").select("id, name, type, statement_keys").order("order"),
        supabase.from("reconciliation_rules").select("*").eq("enabled", true).order("priority"),
      ]);
      setAccounts(accs || []);
      setRules(rls || []);
      const bank = (accs || []).find((a) => a.type === "bank");
      if (bank) setImportAccount(String(bank.id));
    })();
  }, []);

  // ---------- our daily-movement pools ----------
  // Verifying a matched payment also verifies the parts split off it (a deposit
  // partly charged to rent) — they are the same bank movement.
  const txnOrSplits = (ids) =>
    `id.in.(${ids.join(",")}),split_from_transaction_id.in.(${ids.join(",")})`;

  const fetchPools = useCallback(async (from, to) => {
    const gte = `${from}T00:00:00Z`, lt = `${to}T23:59:59Z`;
    const [txnRes, cashRes] = await Promise.all([
      supabase.from("transactions")
        .select("id, amount, type, payment_date, created_at, isDeposit, reservation, customer, billing_entity_type, billing_entity_id, card_type, card_last4, verified_at, split_from_transaction_id, account_rel:transaction_accounts!transactions_account_fkey(name, type)")
        .in("type", ["income", "refund"]).gte("created_at", gte).lt("created_at", lt),
      supabase.from("cash_movements")
        .select("id, amount, created_at, description, reservation_id, type, account_id, to_account_id, account_rel:transaction_accounts!cash_movements_account_id_fkey(name, type), to_account_rel:transaction_accounts!cash_movements_to_account_id_fkey(name, type)")
        .gte("created_at", gte).lt("created_at", lt),
    ]);
    // A deposit partly charged to rent is split into two rows (the charged part
    // carries split_from_transaction_id) but it is still ONE bank movement:
    // fold the parts back into the parent so it matches, and links, as one.
    // The split copies created_at, so both halves always land in this window.
    const splitExtra = {};
    (txnRes.data || []).forEach((t) => {
      if (t.split_from_transaction_id) {
        splitExtra[t.split_from_transaction_id] =
          (splitExtra[t.split_from_transaction_id] || 0) + (t.amount || 0);
      }
    });
    const txns = (txnRes.data || [])
      .filter((t) => !t.split_from_transaction_id)
      .map((t) => (splitExtra[t.id]
        ? { ...t, amount: Math.round(((t.amount || 0) + splitExtra[t.id]) * 100) / 100 }
        : t));
    const cash = cashRes.data || [];
    const cd = (c) => (c.created_at || "").substring(0, 10);
    const income = [
      ...txns.filter((t) => t.type === "income").map((t) => ({
        source: "txn", id: t.id, amount: t.amount, date: txnDate(t),
        accountType: t.account_rel?.type || "", cardType: t.card_type || null, card_last4: t.card_last4 || null,
        reservation: t.reservation, customer: t.customer,
        billing_entity_type: t.billing_entity_type, billing_entity_id: t.billing_entity_id,
        isDeposit: t.isDeposit, verified: !!t.verified_at, label: `Pago #${t.id}${t.isDeposit ? " (depósito)" : ""}`,
      })),
      ...cash.filter((c) => ["income", "deposit", "adjustment"].includes(c.type)).map((c) => ({
        source: "cash", id: c.id, amount: c.amount, date: cd(c), accountType: c.account_rel?.type || "",
        reservation: c.reservation_id, verified: false, label: c.description || `Mov #${c.id}`,
      })),
      ...cash.filter((c) => c.type === "transfer" && c.to_account_id).map((c) => ({
        source: "cash", id: c.id, amount: c.amount, date: cd(c), accountType: c.to_account_rel?.type || "",
        reservation: c.reservation_id, verified: false, label: `Traspaso → ${c.to_account_rel?.name || ""}`,
      })),
    ];
    const outflow = [
      ...txns.filter((t) => t.type === "refund").map((t) => ({
        source: "txn", id: t.id, amount: t.amount, date: txnDate(t), accountType: t.account_rel?.type || "",
        reservation: t.reservation, customer: t.customer,
        billing_entity_type: t.billing_entity_type, billing_entity_id: t.billing_entity_id,
        verified: !!t.verified_at, label: `Reembolso #${t.id}`,
      })),
      ...cash.filter((c) => c.type === "expense").map((c) => ({
        source: "cash", id: c.id, amount: c.amount, date: cd(c), accountType: c.account_rel?.type || "",
        reservation: c.reservation_id, verified: false, label: c.description || `Mov #${c.id}`,
      })),
      ...cash.filter((c) => c.type === "transfer" && c.account_id).map((c) => ({
        source: "cash", id: c.id, amount: c.amount, date: cd(c), accountType: c.account_rel?.type || "",
        reservation: c.reservation_id, verified: false, label: `Traspaso ← ${c.account_rel?.name || ""}`,
      })),
    ];
    return { income, outflow };
  }, []);

  const fetchRfcMap = useCallback(async (rfcs) => {
    const list = [...new Set(rfcs.filter(Boolean))];
    if (!list.length) return {};
    const [cu, co] = await Promise.all([
      supabase.from("customers").select("id, first_name, last_name, rfc").in("rfc", list),
      supabase.from("companies").select("id, name, rfc").in("rfc", list),
    ]);
    const map = {};
    (cu.data || []).forEach((c) => { map[c.rfc] = { entityType: "customer", entityId: c.id, name: `${c.first_name || ""} ${c.last_name || ""}`.trim() }; });
    (co.data || []).forEach((c) => { map[c.rfc] = { entityType: "company", entityId: c.id, name: c.name }; });
    return map;
  }, []);

  // ---------- load ledger + exceptions ----------
  const loadLedger = useCallback(async () => {
    setLoading(true);
    const { start, end } = monthRange(month);
    let q = supabase.from("bank_movements").select("*")
      .gte("movement_date", start).lte("movement_date", end)
      .order("movement_date").order("parent_id", { nullsFirst: true }).order("id");
    if (filterAccount !== "all") q = q.eq("account_id", parseInt(filterAccount, 10));
    const { data: rows } = await q;
    let movs = rows || [];
    // Resolve payer RFC → known client name so SPEI rows show "Asake Produce"
    // instead of a bare "RASTREO <clave>". Display-only; no data is written.
    const rfcMap = await fetchRfcMap(movs.map((m) => m.payer_rfc));
    movs = movs.map((m) => (m.payer_rfc && rfcMap[m.payer_rfc]
      ? { ...m, client_name: rfcMap[m.payer_rfc].name } : m));
    setMovements(movs);

    // exceptions need our payment pools for the window
    const { income, outflow } = await fetchPools(addDaysISO(start, -10), addDaysISO(end, 10));
    const linkedTxn = new Set(movs.map((m) => m.transaction_id).filter(Boolean));
    const linkedCash = new Set(movs.map((m) => m.cash_movement_id).filter(Boolean));
    const used = (r) => (r.source === "txn" ? linkedTxn.has(r.id) : linkedCash.has(r.id)) || r.verified;
    const leaves = movs.filter((m) => !movs.some((x) => x.parent_id === m.id)); // no children
    const uIn = income.filter((r) => !used(r));
    const uOut = outflow.filter((r) => !used(r));
    exDataRef.current = { unusedIncome: uIn, unusedOutflow: uOut };
    setExceptions(computeExceptions({ leaves, unusedIncome: uIn, unusedOutflow: uOut, today: todayISO() }));
    setLoading(false);
  }, [month, filterAccount, fetchPools, fetchRfcMap]);

  useEffect(() => { if (accounts.length) loadLedger(); }, [accounts.length, loadLedger]);

  // ---------- import ----------
  const readFile = (files, setter) => {
    const file = files?.[0]; if (!file) return;
    file.text().then((text) => setter({ name: file.name, text }));
  };
  // Bank statement drop: read + auto-detect the account from the file contents.
  const onBankDrop = (files) => {
    const file = files?.[0]; if (!file) return;
    file.text().then((text) => {
      setBankFile({ name: file.name, text });
      const det = detectAccount(text);
      setImportAccount(det ? String(det.id) : "");
    });
  };

  const processImport = async () => {
    if (!bankFile && !tpvFile) { alert("Sube el archivo del banco y/o el reporte TPV."); return; }
    if (!importAccount) { alert("Selecciona la cuenta a la que pertenece el archivo."); return; }
    setBusy("import");
    setProgress({ pct: 8, label: "Analizando archivo…" });
    try {
      const accId = parseInt(importAccount, 10);
      const mvs = bankFile ? normalizeBank(bankFile.text).movements : [];
      const tpvSales = tpvFile && isAfirmeTpv(tpvFile.text) ? parseAfirmeTpv(tpvFile.text).sales : [];
      if (!mvs.length && !tpvSales.length) {
        alert(bankFile ? "No se detectaron movimientos en el archivo del banco." : "No se detectaron ventas en el reporte TPV.");
        setProgress(null); setBusy(""); return;
      }

      // 1) upsert parent/leaf bank lines (only when a bank file was uploaded)
      const parentPayloads = mvs.map((m) => ({
        account_id: accId, movement_date: m.date, amount: m.amount,
        direction: m.credit ? "credit" : "charge",
        // Prefer the parsed SPEI concepto (payer's note / our devolución text);
        // keep the full narrative in notes so nothing is lost.
        concepto: m.concepto || m.shortConcept || m.description || "",
        notes: m.description || null,
        clave_rastreo: m.claveRastreo || null, reference: m.reference || null,
        payer_rfc: m.payerRfc || null, payer_name: m.payerName || null, counterparty_clabe: m.payerClabe || null,
        source: "bank_file", category: categorize(m),
        dedupe_hash: dedupeHash(accId, { ...m, source: "bank_file" }),
        created_by: userId,
      }));
      if (parentPayloads.length) {
        const CH = 50;
        for (let i = 0; i < parentPayloads.length; i += CH) {
          const { error: pErr } = await supabase.from("bank_movements")
            .upsert(parentPayloads.slice(i, i + CH), { onConflict: "dedupe_hash", ignoreDuplicates: true });
          if (pErr) throw new Error(pErr.message);
          const done = Math.min(i + CH, parentPayloads.length);
          setProgress({ pct: 10 + Math.round(45 * done / parentPayloads.length), label: `Guardando movimientos… ${done}/${parentPayloads.length}` });
        }
      }

      // 2) TPV sub-movements. Subs always attach under a bank VENTA line: when a
      // bank file is in this same import we wire them to those just-saved rows;
      // when the TPV report is imported on its own we attach to VENTA lines
      // already in the ledger for this account.
      let subPayloads = [];
      if (tpvSales.length) {
        let subs = [], parentIdOf;
        if (parentPayloads.length) {
          const phashes = parentPayloads.map((p) => p.dedupe_hash);
          const idByHash = {};
          for (let i = 0; i < phashes.length; i += 200) {
            const { data } = await supabase.from("bank_movements").select("id, dedupe_hash").in("dedupe_hash", phashes.slice(i, i + 200));
            (data || []).forEach((r) => { idByHash[r.dedupe_hash] = r.id; });
          }
          subs = assembleTpvSubs(mvs, tpvSales).subs;
          parentIdOf = (s) => idByHash[parentPayloads[s.parentKey].dedupe_hash] || null;
        } else {
          // TPV-only: match against existing card-sale bank lines in the ledger.
          const sd = tpvSales.map((s) => s.depositDate || s.saleDate).filter(Boolean).sort();
          const from = addDaysISO(sd[0], -3), to = addDaysISO(sd[sd.length - 1], 3);
          const { data: existing } = await supabase.from("bank_movements")
            .select("id, movement_date, amount, concepto, direction")
            .eq("account_id", accId).eq("category", CATEGORY.CARD_SALE).eq("direction", "credit")
            .gte("movement_date", from).lte("movement_date", to);
          const bankLines = (existing || []).map((r) => ({
            date: r.movement_date, amount: r.amount, credit: true,
            shortConcept: r.concepto || "", description: r.concepto || "", _id: r.id,
          }));
          subs = assembleTpvSubs(bankLines, tpvSales).subs;
          parentIdOf = (s) => bankLines[s.parentKey]?._id || null;
          if (!subs.length) setProgress({ pct: 55, label: "Sin líneas de venta TPV en el banco para conciliar…" });
        }
        subPayloads = subs.map((s) => ({
          account_id: accId, movement_date: s.date, amount: s.amount, direction: "credit",
          concepto: s.concepto, card_last4: s.card_last4, card_type: s.card_type,
          auth_code: s.auth, terminal: s.terminal, source: "tpv_report",
          parent_id: parentIdOf(s),
          category: s.category,
          dedupe_hash: dedupeHash(accId, { date: s.date, amount: s.amount, credit: true, source: "tpv_report", auth: s.auth, reference: s.card_last4 }),
          created_by: userId,
        }));
        if (subPayloads.length) {
          setProgress({ pct: 60, label: `Guardando ${subPayloads.length} sub-movimientos TPV…` });
          const { error: sErr } = await supabase.from("bank_movements").upsert(subPayloads, { onConflict: "dedupe_hash", ignoreDuplicates: true });
          if (sErr) throw new Error(sErr.message);
        }
      }

      const allDates = [...mvs.map((m) => m.date), ...tpvSales.map((s) => s.saleDate || s.depositDate)].filter(Boolean).sort();
      const importedMonth = allDates[allDates.length - 1].substring(0, 7);
      setBankFile(null); setTpvFile(null);
      setProgress({ pct: 72, label: "Conciliando movimientos…" });
      await runMatch(importedMonth);
      setProgress({ pct: 92, label: "Cargando…" });
      setFilterAccount(String(accId));
      setMonth(importedMonth);
      await loadLedger();
      setProgress({ pct: 100, label: "Listo" });
      window.setTimeout(() => setProgress(null), 700);
      alert(`Importado en ${accountName(accId)}: ${parentPayloads.length} movimientos, ${subPayloads.length} sub-movimientos TPV.`);
    } catch (e) { alert("Error al importar: " + (e.message || e)); setProgress(null); }
    finally { setBusy(""); }
  };

  // ---------- matching pass over unmatched leaves/subs ----------
  const runMatch = useCallback(async (targetMonth = month) => {
    const { start, end } = monthRange(targetMonth);
    const from = addDaysISO(start, -15), to = addDaysISO(end, 15);
    // all movements in window (all accounts, for transfer pairing)
    const { data: all } = await supabase.from("bank_movements").select("*")
      .gte("movement_date", from).lte("movement_date", to);
    const movs = all || [];
    const childIds = new Set(movs.filter((m) => m.parent_id).map((m) => m.parent_id));
    const leaves = movs.filter((m) => !childIds.has(m.id) && !m.verified && m.status === "unmatched");

    const { income, outflow } = await fetchPools(from, to);
    const usedTxn = new Set(movs.map((m) => m.transaction_id).filter(Boolean));
    const usedCash = new Set(movs.map((m) => m.cash_movement_id).filter(Boolean));
    income.forEach((r) => (r.used = (r.source === "txn" ? usedTxn.has(r.id) : usedCash.has(r.id)) || r.verified));
    outflow.forEach((r) => (r.used = (r.source === "txn" ? usedTxn.has(r.id) : usedCash.has(r.id)) || r.verified));
    const rfcMap = await fetchRfcMap(leaves.map((l) => l.payer_rfc));

    const updates = [];
    // Pass 1: rules; Pass 2: unique-key / amount-date.
    for (const lf of leaves.sort((a, b) => a.movement_date.localeCompare(b.movement_date) || a.id - b.id)) {
      const rule = ruleFor({ category: lf.category, concepto: lf.concepto, account_id: lf.account_id, direction: lf.direction }, rules);
      if (rule) { updates.push({ id: lf.id, status: "auto_matched", match_method: "rule", auto_rule_id: rule.id, match_pass: 1, flag: null }); continue; }
      if (lf.category === CATEGORY.CLIP) continue; // a Clip deposit is reconciled by its subs (Clip sync), not directly
      const pool = lf.direction === "credit" ? income : outflow;
      const client = lf.payer_rfc ? rfcMap[lf.payer_rfc] : null;
      const node = { category: lf.category, date: lf.movement_date, amount: lf.amount, card_type: lf.card_type, card_last4: lf.card_last4 };
      const res = matchPayment(node, pool, { client });
      if (res?.match) {
        res.match.used = true;
        updates.push({
          id: lf.id, status: "auto_matched", match_method: res.method, confidence: res.confidence, match_pass: 2,
          transaction_id: res.match.source === "txn" ? res.match.id : null,
          cash_movement_id: res.match.source === "cash" ? res.match.id : null, flag: null,
        });
      } else if (res?.ambiguous) {
        updates.push({ id: lf.id, flag: "ambiguous" });
      }
    }
    for (const u of updates) await supabase.from("bank_movements").update(u).eq("id", u.id);

    // Pass 3: internal transfer pairing (+ auto-create the cash transfer).
    const fresh = movs.map((m) => updates.find((u) => u.id === m.id) ? { ...m, ...updates.find((u) => u.id === m.id) } : m);
    const pairs = pairTransfers(fresh.filter((m) => !m.transfer_pair_id));
    for (const { from: f, to: t } of pairs) {
      const { data: cm } = await supabase.from("cash_movements").insert({
        type: "transfer", amount: f.amount, account_id: f.account_id, to_account_id: t.account_id,
        description: `Traspaso conciliado ${formatDate(f.movement_date)}`, created_by: userId,
      }).select().single();
      await supabase.from("bank_movements").update({ status: "auto_matched", match_method: "transfer", transfer_pair_id: t.id, cash_movement_id: cm?.id || null, match_pass: 3, flag: null }).eq("id", f.id);
      await supabase.from("bank_movements").update({ status: "auto_matched", match_method: "transfer", transfer_pair_id: f.id, cash_movement_id: cm?.id || null, match_pass: 3, flag: null }).eq("id", t.id);
    }

    // Pass 4: parent auto-complete — a parent (batched VENTA / Clip deposit) is
    // reconciled once all its subs are matched or verified.
    const statusOf = (m) => { const u = updates.find((x) => x.id === m.id); return (u && u.status) || m.status; };
    for (const pid of childIds) {
      const kids = movs.filter((m) => m.parent_id === pid);
      const parent = movs.find((m) => m.id === pid);
      if (!parent || parent.verified || parent.status === "auto_matched" || !kids.length) continue;
      if (kids.every((k) => k.verified || statusOf(k) === "auto_matched")) {
        await supabase.from("bank_movements").update({ status: "auto_matched", match_method: "parent", match_pass: 4, flag: null }).eq("id", pid);
      }
    }
  }, [month, rules, fetchPools, fetchRfcMap, userId]);

  // ---------- Clip sync: pull settlements, split into subs under their bank deposit ----------
  const clipSync = async () => {
    setBusy("clip");
    try {
      const { start, end } = monthRange(month);
      const settlements = await getClipSettlements(start, end);
      if (!settlements.length) { alert("Sin liquidaciones Clip en el periodo."); setBusy(""); return; }
      const { data: bankRows } = await supabase.from("bank_movements").select("*")
        .gte("movement_date", addDaysISO(start, -5)).lte("movement_date", addDaysISO(end, 5))
        .eq("category", "clip_settlement").eq("direction", "credit").is("parent_id", null);
      const parents = bankRows || [];
      const usedParent = new Set();
      let createdSubs = 0, unmatchedDeposits = 0;
      for (const s of settlements) {
        const detail = await getClipSettlementDetail(s.settlement_report_id);
        const { net, commission, disbursementDate, subs } = clipSettlementToSubs(detail || s);
        const parent = parents.find((b) => !usedParent.has(b.id) && Math.abs(b.amount - net) < 0.5 && absDayDiff(b.movement_date, disbursementDate) <= 4);
        if (!parent) { unmatchedDeposits++; continue; }
        usedParent.add(parent.id);
        const payload = subs.map((sub) => ({
          account_id: parent.account_id, movement_date: sub.date, amount: sub.amount, direction: "credit",
          concepto: sub.concepto, card_last4: sub.card_last4, card_type: sub.card_type, auth_code: sub.auth,
          source: "clip_api", parent_id: parent.id, category: sub.category,
          dedupe_hash: dedupeHash(parent.account_id, { date: sub.date, amount: sub.amount, credit: true, source: "clip_api", auth: sub.auth }),
          created_by: userId,
        }));
        if (commission > 0.01) payload.push({
          account_id: parent.account_id, movement_date: disbursementDate, amount: commission, direction: "charge",
          concepto: "Comisión Clip", source: "clip_api", parent_id: parent.id, category: "pos_commission",
          dedupe_hash: dedupeHash(parent.account_id, { date: disbursementDate, amount: -commission, credit: false, source: "clip_api", reference: `clipcom-${parent.id}` }),
          created_by: userId,
        });
        if (payload.length) {
          await supabase.from("bank_movements").upsert(payload, { onConflict: "dedupe_hash", ignoreDuplicates: true });
          createdSubs += payload.length;
        }
      }
      await runMatch();
      await loadLedger();
      alert(`Clip: ${createdSubs} sub-movimientos creados; ${unmatchedDeposits} liquidaciones sin línea bancaria (GANANCIAS CLIP).`);
    } catch (e) { alert("Error al sincronizar Clip: " + (e.message || e)); }
    finally { setBusy(""); }
  };

  const reMatch = async () => { setBusy("match"); try { await runMatch(); await loadLedger(); } finally { setBusy(""); } };

  // ---------- verify ----------
  const setVerified = async (ids, value) => {
    if (!ids.length) return;
    await supabase.from("bank_movements").update({
      verified: value, status: value ? "verified" : "unmatched",
      verified_at: value ? new Date().toISOString() : null, verified_by: value ? userId : null,
    }).in("id", ids);
    const txnIds = movements.filter((m) => ids.includes(m.id) && m.transaction_id).map((m) => m.transaction_id);
    if (txnIds.length) {
      await supabase.from("transactions").update({
        verified_at: value ? new Date().toISOString() : null, verified_by: value ? userId : null,
      }).or(txnOrSplits(txnIds));
    }
  };
  // Patch verified rows in place + recompute exceptions locally — no full reload.
  const applyVerifyLocal = (ids, value) => {
    const idset = new Set(ids);
    const next = movementsRef.current.map((m) =>
      idset.has(m.id)
        ? { ...m, verified: value, verified_at: value ? new Date().toISOString() : null,
            status: value ? "verified" : (m.match_method ? "auto_matched" : "unmatched") }
        : m);
    movementsRef.current = next;
    setMovements(next);
    const leaves = next.filter((m) => !next.some((x) => x.parent_id === m.id));
    setExceptions(computeExceptions({
      leaves, unusedIncome: exDataRef.current.unusedIncome, unusedOutflow: exDataRef.current.unusedOutflow,
      today: todayISO(),
    }));
  };
  const toggleVerify = async (row) => {
    setBusy(`v${row.id}`);
    const value = !row.verified;
    try { await setVerified([row.id], value); applyVerifyLocal([row.id], value); }
    finally { setBusy(""); }
  };
  const verifyAllMatched = async () => {
    const ids = movements.filter((m) => !m.verified && m.status === "auto_matched").map((m) => m.id);
    if (!ids.length) return;
    setBusy("bulk"); try { await setVerified(ids, true); applyVerifyLocal(ids, true); } finally { setBusy(""); }
  };

  // ---------- register an unmatched bank credit as a payment ----------
  const doRegister = async () => {
    const row = regRow; setBusy("reg");
    try {
      let billing = null;
      if (row.payer_rfc) { const map = await fetchRfcMap([row.payer_rfc]); billing = map[row.payer_rfc] || null; }
      const { data: txn, error } = await supabase.from("transactions").insert({
        type: "income", amount: row.amount, payment_date: row.movement_date, account: row.account_id,
        billing_entity_type: billing?.entityType || null, billing_entity_id: billing?.entityId || null,
        customer: billing?.entityType === "customer" ? billing.entityId : null,
        reservation: regForm.reservation ? parseInt(regForm.reservation, 10) : null,
        isDeposit: false, unallocated_amount: row.amount, invoiced: false,
        verified_at: new Date().toISOString(), verified_by: userId,
      }).select().single();
      if (error) throw new Error(error.message);
      await supabase.from("bank_movements").update({
        transaction_id: txn.id, status: "verified", match_method: "manual", match_pass: 3,
        verified: true, verified_at: new Date().toISOString(), verified_by: userId, flag: null,
      }).eq("id", row.id);
      setRegRow(null); await loadLedger();
    } catch (e) { alert("Error al registrar: " + (e.message || e)); }
    finally { setBusy(""); }
  };

  // ---------- tie a bank line to already-recorded payment(s) ----------
  // The auto-matcher only handles 1:1 amount matches; SPEI deposits that fund
  // several reservation payments at once (e.g. one $22,120 credit = two $11,060
  // pagos) never auto-reconcile. Here the operator picks the real payments that
  // add up to the bank amount. One payment links the row directly; several split
  // the parent into sub-movements each tied to its transaction (parent then ties).
  const openMatch = async (m) => {
    setBusy(`open${m.id}`);
    try {
      let client = null;
      if (m.payer_rfc) { const map = await fetchRfcMap([m.payer_rfc]); client = map[m.payer_rfc] || null; }
      const belongs = (r) => client && (
        (r.billing_entity_type === client.entityType && r.billing_entity_id === client.entityId) ||
        (client.entityType === "customer" && r.customer === client.entityId));
      const pool = (m.direction === "credit" ? exDataRef.current.unusedIncome : exDataRef.current.unusedOutflow) || [];
      let cands = pool
        .filter((r) => absDayDiff(r.date, m.movement_date) <= 21)
        .map((r) => ({ ...r, key: `${r.source}:${r.id}`, isClient: !!belongs(r), diff: dayDiff(r.date, m.movement_date) }))
        .sort((a, b) => (b.isClient - a.isClient) || (Math.abs(a.diff) - Math.abs(b.diff)) || (Math.abs(a.amount - m.amount) - Math.abs(b.amount - m.amount)));

      // Resolve customer/company names so the "Por cliente" grouping reads nicely.
      const custIds = new Set(), compIds = new Set();
      cands.forEach((r) => {
        if (r.billing_entity_type === "company" && r.billing_entity_id) compIds.add(r.billing_entity_id);
        else if (r.billing_entity_type === "customer" && r.billing_entity_id) custIds.add(r.billing_entity_id);
        else if (r.customer) custIds.add(r.customer);
      });
      const [cuRes, coRes] = await Promise.all([
        custIds.size ? supabase.from("customers").select("id, first_name, last_name, razon_social").in("id", [...custIds]) : Promise.resolve({ data: [] }),
        compIds.size ? supabase.from("companies").select("id, name").in("id", [...compIds]) : Promise.resolve({ data: [] }),
      ]);
      const custName = {}; (cuRes.data || []).forEach((c) => { custName[c.id] = c.razon_social || `${c.first_name || ""} ${c.last_name || ""}`.trim() || `Cliente #${c.id}`; });
      const compName = {}; (coRes.data || []).forEach((c) => { compName[c.id] = c.name || `Empresa #${c.id}`; });
      cands = cands.map((r) => {
        const isCo = r.billing_entity_type === "company" && r.billing_entity_id;
        const custId = isCo ? null : (r.billing_entity_type === "customer" && r.billing_entity_id) || r.customer || null;
        return {
          ...r,
          resKey: r.reservation ? `res:${r.reservation}` : "none",
          resLabel: r.reservation ? `Reserva ${r.reservation}` : "Sin reserva",
          custKey: isCo ? `company:${r.billing_entity_id}` : custId ? `customer:${custId}` : "none",
          custLabel: isCo ? (compName[r.billing_entity_id] || `Empresa #${r.billing_entity_id}`)
            : custId ? (custName[custId] || `Cliente #${custId}`) : "Sin cliente",
        };
      });
      // Preselect: an exact single (client first), else a client-matched subset that sums to the amount.
      const sel = new Set();
      const exact = cands.find((r) => Math.abs(r.amount - m.amount) < EPS && r.isClient)
        || cands.find((r) => Math.abs(r.amount - m.amount) < EPS);
      if (exact) sel.add(exact.key);
      else {
        let sum = 0;
        for (const r of cands.filter((r) => r.isClient)) {
          if (sum + r.amount <= m.amount + EPS) { sel.add(r.key); sum += r.amount; }
          if (Math.abs(sum - m.amount) < EPS) break;
        }
        if (Math.abs(sum - m.amount) >= EPS) sel.clear(); // no clean subset → let the user choose
      }
      setMatchCands(cands); setMatchSel(sel); setMatchCombos(null); setMatchRow(m);
    } finally { setBusy(""); }
  };
  // Enumerate, per customer, every combination of that client's payments that
  // sums to the bank amount. Per-customer (not mixed) so a combination maps to a
  // single payer. Sorted fewest-payments-first; empty result → "ninguna".
  const computeCombos = () => {
    const target = matchRow.amount;
    const byCust = new Map();
    matchCands.forEach((c) => { if (!byCust.has(c.custKey)) byCust.set(c.custKey, []); byCust.get(c.custKey).push(c); });
    const out = [];
    let capped = false;
    for (const items of byCust.values()) {
      const { results, capped: c } = subsetSumCombos(items, target, { maxResults: 25 });
      capped = capped || c;
      results.forEach((combo) => out.push({ custLabel: combo[0].custLabel, items: combo, keys: combo.map((x) => x.key) }));
      if (out.length >= 100) { capped = true; break; }
    }
    out.sort((a, b) => a.items.length - b.items.length);
    setMatchCombos({ list: out, capped });
  };
  const applyCombo = (combo) => setMatchSel(new Set(combo.keys));
  const toggleCand = (key) => setMatchSel((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleCandGroup = (items) => setMatchSel((s) => {
    const n = new Set(s);
    const all = items.every((i) => n.has(i.key));
    items.forEach((i) => (all ? n.delete(i.key) : n.add(i.key)));
    return n;
  });
  // Patch the ledger in place after a conciliation — no full re-query. Drops the
  // consumed payments from the exception pools and recomputes exceptions locally.
  const applyMatchLocal = (m, chosen, newSubs = []) => {
    const now = new Date().toISOString();
    let next;
    if (newSubs.length) {
      next = movementsRef.current.map((x) => x.id === m.id
        ? { ...x, status: "verified", verified: true, verified_at: now, match_method: "parent", flag: null }
        : x);
      next = [...next, ...newSubs];
    } else {
      const c = chosen[0];
      next = movementsRef.current.map((x) => x.id === m.id
        ? { ...x, status: "verified", verified: true, verified_at: now, match_method: "manual", flag: null,
            transaction_id: c.source === "txn" ? c.id : x.transaction_id,
            cash_movement_id: c.source === "cash" ? c.id : x.cash_movement_id }
        : x);
    }
    movementsRef.current = next; setMovements(next);
    const consumed = new Set(chosen.map((c) => c.key));
    const drop = (arr) => arr.filter((r) => !consumed.has(`${r.source}:${r.id}`));
    exDataRef.current = { unusedIncome: drop(exDataRef.current.unusedIncome), unusedOutflow: drop(exDataRef.current.unusedOutflow) };
    const leaves = next.filter((mv) => !next.some((x) => x.parent_id === mv.id));
    setExceptions(computeExceptions({
      leaves, unusedIncome: exDataRef.current.unusedIncome, unusedOutflow: exDataRef.current.unusedOutflow, today: todayISO(),
    }));
  };
  const doMatchExisting = async () => {
    const m = matchRow;
    const chosen = matchCands.filter((c) => matchSel.has(c.key));
    if (!chosen.length) { alert("Selecciona al menos un pago."); return; }
    const now = new Date().toISOString();
    setBusy("matchsave");
    try {
      let newSubs = [];
      if (chosen.length === 1 && Math.abs(chosen[0].amount - m.amount) < EPS) {
        const c = chosen[0];
        const { error } = await supabase.from("bank_movements").update({
          transaction_id: c.source === "txn" ? c.id : null,
          cash_movement_id: c.source === "cash" ? c.id : null,
          status: "verified", match_method: "manual", match_pass: 3,
          verified: true, verified_at: now, verified_by: userId, flag: null,
        }).eq("id", m.id);
        if (error) throw new Error(error.message);
      } else {
        // Split: one verified sub per chosen payment, then tie the parent.
        const payload = chosen.map((c) => ({
          account_id: m.account_id, movement_date: m.movement_date, amount: c.amount, direction: m.direction,
          concepto: c.label, source: "manual_split", parent_id: m.id, category: m.category,
          transaction_id: c.source === "txn" ? c.id : null,
          cash_movement_id: c.source === "cash" ? c.id : null,
          status: "verified", match_method: "manual", match_pass: 3,
          verified: true, verified_at: now, verified_by: userId,
          dedupe_hash: dedupeHash(m.account_id, { date: m.movement_date, amount: c.amount, credit: m.direction === "credit", source: "manual_split", reference: `split-${m.id}-${c.key}` }),
          created_by: userId,
        }));
        const { data: subs, error: sErr } = await supabase.from("bank_movements")
          .upsert(payload, { onConflict: "dedupe_hash", ignoreDuplicates: true }).select();
        if (sErr) throw new Error(sErr.message);
        newSubs = subs || [];
        const { error: pErr } = await supabase.from("bank_movements").update({
          status: "verified", match_method: "parent", match_pass: 4,
          verified: true, verified_at: now, verified_by: userId, flag: null,
        }).eq("id", m.id);
        if (pErr) throw new Error(pErr.message);
      }
      const txnIds = chosen.filter((c) => c.source === "txn").map((c) => c.id);
      if (txnIds.length) await supabase.from("transactions").update({ verified_at: now, verified_by: userId }).or(txnOrSplits(txnIds));
      setMatchRow(null);
      // A split whose subs all deduped away (re-conciliation) has nothing to patch
      // in — fall back to a reload so the existing rows surface.
      if (chosen.length > 1 && !newSubs.length) await loadLedger();
      else applyMatchLocal(m, chosen, newSubs);
    } catch (e) { alert("Error al conciliar: " + (e.message || e)); }
    finally { setBusy(""); }
  };

  // ---------- fix a wrong category ----------
  // Categorization is a regex guess over the concepto; when it's off (e.g. an
  // "OTRO" that's really a supplier payment, or a mislabeled TPV line) the operator
  // corrects it here. Re-conciliar afterwards to re-apply rules with the new label.
  const openCat = (m) => { setCatValue(m.category); setCatRow(m); };
  const saveCat = async () => {
    const m = catRow; if (!m || catValue === m.category) { setCatRow(null); return; }
    setBusy("cat");
    try {
      const { error } = await supabase.from("bank_movements").update({ category: catValue }).eq("id", m.id);
      if (error) throw new Error(error.message);
      const next = movementsRef.current.map((x) => (x.id === m.id ? { ...x, category: catValue } : x));
      movementsRef.current = next; setMovements(next);
      const leaves = next.filter((mv) => !next.some((x) => x.parent_id === mv.id));
      setExceptions(computeExceptions({
        leaves, unusedIncome: exDataRef.current.unusedIncome, unusedOutflow: exDataRef.current.unusedOutflow, today: todayISO(),
      }));
      setCatRow(null);
    } catch (e) { alert("Error al cambiar categoría: " + (e.message || e)); }
    finally { setBusy(""); }
  };

  // ---------- build display tree ----------
  const byParent = {};
  movements.forEach((m) => { if (m.parent_id) (byParent[m.parent_id] = byParent[m.parent_id] || []).push(m); });
  const presentIds = new Set(movements.map((m) => m.id));
  const isReconciled = (m) => m.verified || m.status === "auto_matched" || m.status === "verified";
  const rowVisible = (m) =>
    (!hideReconciled || !isReconciled(m)) &&
    (filterCategory === "all" || m.category === filterCategory);
  const rowsFlat = [];
  movements.filter((m) => !m.parent_id || !presentIds.has(m.parent_id)).forEach((m) => {
    const kids = byParent[m.id] || [];
    const visKids = kids.filter(rowVisible);
    // Hide a filtered-out group; keep a parent whose children still qualify.
    if (!rowVisible(m) && visKids.length === 0) return;
    rowsFlat.push({ m, depth: 0, hasKids: !!kids.length });
    visKids.forEach((c) => rowsFlat.push({ m: c, depth: 1, hasKids: false }));
  });

  // Category filter options — only those present in the loaded ledger.
  const categoryOptions = [
    { label: "Todas las categorías", value: "all" },
    ...[...new Set(movements.map((m) => m.category).filter(Boolean))]
      .map((c) => ({ label: CATEGORY_LABEL[c] || c, value: c }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const allCategoryOptions = Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ label, value }));

  const counts = {
    total: movements.length,
    verified: movements.filter((m) => m.verified).length,
    matched: movements.filter((m) => !m.verified && m.status === "auto_matched").length,
    pending: movements.filter((m) => !m.verified && m.status === "unmatched").length,
  };

  const statusBadge = (m, hasKids) => {
    if (m.verified) return <Badge tone="success">Verificado</Badge>;
    if (hasKids) {
      const kids = byParent[m.id] || [];
      const done = kids.filter((k) => k.verified || k.status === "auto_matched").length;
      return <Badge tone={done === kids.length ? "info" : "attention"}>{done}/{kids.length} sub</Badge>;
    }
    if (m.flag === "ambiguous") return <Badge tone="warning">Ambiguo</Badge>;
    if (m.status === "auto_matched") return <Badge tone="info">Conciliado</Badge>;
    return <Badge tone="critical">Sin conciliar</Badge>;
  };

  const accountName = (id) => accounts.find((a) => a.id === id)?.name || id;

  const matchSum = matchCands.filter((c) => matchSel.has(c.key)).reduce((a, c) => a + c.amount, 0);
  const matchDiff = matchRow ? matchRow.amount - matchSum : 0;
  const matchTied = matchRow && Math.abs(matchDiff) < EPS && matchSel.size > 0;

  const renderMatchCand = (c) => (
    <Box key={c.key} padding="200" borderBlockEndWidth="025" borderColor="border">
      <InlineStack align="space-between" blockAlign="center" wrap={false}>
        <InlineStack gap="200" blockAlign="center">
          <Checkbox label="" labelHidden checked={matchSel.has(c.key)} onChange={() => toggleCand(c.key)} />
          <BlockStack gap="0">
            <Text variant="bodySm">{c.label}{c.reservation ? ` · Reserva ${c.reservation}` : ""}</Text>
            <Text variant="bodySm" tone="subdued">{formatDate(c.date)}{c.diff ? ` · ${c.diff > 0 ? "+" : ""}${c.diff}d` : ""}{c.isClient ? "" : " · otro cliente"}</Text>
          </BlockStack>
        </InlineStack>
        <InlineStack gap="200" blockAlign="center">
          {c.isClient && <Badge tone="success" size="small">RFC</Badge>}
          <Text variant="bodyMd"><strong>{formatPrice(c.amount)}</strong></Text>
        </InlineStack>
      </InlineStack>
    </Box>
  );
  const matchGroups = () => {
    if (matchGroup === "none") return [{ key: "all", label: null, items: matchCands }];
    const keyer = matchGroup === "reservation" ? (r) => r.resKey : (r) => r.custKey;
    const labeler = matchGroup === "reservation" ? (r) => r.resLabel : (r) => r.custLabel;
    const map = new Map();
    matchCands.forEach((r) => {
      const k = keyer(r);
      if (!map.has(k)) map.set(k, { key: k, label: labeler(r), items: [] });
      map.get(k).items.push(r);
    });
    return [...map.values()];
  };

  return (
    <AppWrapper>
      <Page fullWidth title="Conciliación Bancaria"
        subtitle="Control diario: cada movimiento del banco identificado y cada pago verificado">
        <BlockStack gap="400">
          {/* filters */}
          <Card>
            <InlineStack gap="300" blockAlign="end" wrap>
              <Box minWidth="200px">
                <Select label="Cuenta" value={filterAccount} onChange={setFilterAccount}
                  options={[{ label: "Todas las cuentas", value: "all" }, ...accounts.map((a) => ({ label: a.name, value: String(a.id) }))]} />
              </Box>
              <Box minWidth="180px">
                <Select label="Mes" value={month} onChange={setMonth}
                  options={recentMonths(12).map((ym) => ({ label: monthLabel(ym), value: ym }))} />
              </Box>
              <Box minWidth="200px">
                <Select label="Categoría" value={filterCategory} onChange={setFilterCategory} options={categoryOptions} />
              </Box>
              <Button onClick={reMatch} loading={busy === "match"}>Re-conciliar</Button>
              <Button onClick={clipSync} loading={busy === "clip"}>Sincronizar Clip</Button>
              <Box paddingBlockStart="150">
                <Checkbox label="Ocultar conciliados" checked={hideReconciled} onChange={setHideReconciled} />
              </Box>
            </InlineStack>
          </Card>

          {/* exceptions */}
          {exceptions && (
            <InlineGrid columns={4} gap="300">
              <ExCard tone="critical" title="Registrado, sin banco" count={exceptions.recordedNotInBank.length} hint="Posible pago falso / robo (>3 días)" />
              <ExCard tone="critical" title="En banco, sin registrar" count={exceptions.inBankNotRecorded.length} hint="Cliente pagó sin avisar" />
              <ExCard tone="warning" title="Efectivo no depositado" count={exceptions.cashNotDeposited.length} hint="Cobrado, aún sin depositar" />
              <ExCard tone="warning" title="Monto difiere" count={exceptions.amountMismatch.length} hint="Depósito parcial / corto" />
            </InlineGrid>
          )}

          {/* import */}
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd">Importar movimientos</Text>
              <Text tone="subdued" variant="bodySm">Sube el estado de cuenta del banco y/o el reporte TPV — puedes importar cualquiera de los dos por separado.</Text>
              <InlineGrid columns={2} gap="300">
                <DropZone accept=".txt,.sta,text/plain" type="file" allowMultiple={false} onDrop={onBankDrop}>
                  {bankFile ? <Box padding="400"><Text>{bankFile.name}</Text><Text tone="subdued" variant="bodySm">Estado de cuenta</Text></Box>
                    : <DropZone.FileUpload actionTitle="Estado de cuenta (MultiCash / MT940)" actionHint="Arrastra el .txt — la cuenta se detecta sola" />}
                </DropZone>
                <DropZone accept=".csv,text/csv" type="file" allowMultiple={false} onDrop={(f) => readFile(f, setTpvFile)}>
                  {tpvFile ? <Box padding="400"><Text>{tpvFile.name}</Text><Text tone="subdued" variant="bodySm">Reporte TPV</Text></Box>
                    : <DropZone.FileUpload actionTitle="Reporte TPV Afirme" actionHint="Comprobante_de_transaccion.csv" />}
                </DropZone>
              </InlineGrid>
              {(bankFile || tpvFile) && (
                <InlineStack align="space-between" blockAlign="end" gap="300">
                  <Box minWidth="260px">
                    <Select
                      label="Cuenta"
                      labelInline
                      placeholder="Selecciona la cuenta"
                      options={accounts.filter((a) => a.type === "bank").map((a) => ({ label: a.name, value: String(a.id) }))}
                      value={importAccount}
                      onChange={setImportAccount}
                    />
                  </Box>
                  <Button variant="primary" onClick={processImport} loading={busy === "import"} disabled={(!bankFile && !tpvFile) || !importAccount}>Procesar y conciliar</Button>
                </InlineStack>
              )}
              {progress && (
                <BlockStack gap="100">
                  <ProgressBar progress={progress.pct} size="small" tone="primary" />
                  <Text tone="subdued" variant="bodySm">{progress.label} ({progress.pct}%)</Text>
                </BlockStack>
              )}
            </BlockStack>
          </Card>

          {loading && <Spinner size="small" />}

          {/* ledger */}
          {!loading && (
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingMd">{filterAccount === "all" ? "Todas las cuentas" : accountName(parseInt(filterAccount, 10))} · {monthLabel(month)}</Text>
                  <InlineStack gap="200" blockAlign="center">
                    <Text tone="subdued" variant="bodySm">{counts.verified}/{counts.total} verificados · {counts.pending} sin conciliar</Text>
                    <Button onClick={verifyAllMatched} loading={busy === "bulk"} disabled={counts.matched === 0}>Verificar conciliados ({counts.matched})</Button>
                  </InlineStack>
                </InlineStack>

                {movements.length === 0 ? (
                  <EmptyState heading="Sin movimientos" image=""><p>Importa un estado de cuenta para empezar.</p></EmptyState>
                ) : (
                    <IndexTable resourceName={{ singular: "mov", plural: "movs" }} selectable={false} itemCount={rowsFlat.length}
                      headings={[{ title: "Fecha" }, { title: "Cuenta" }, { title: "Concepto" }, { title: "Cargo" }, { title: "Abono" }, { title: "Categoría" }, { title: "Estado" }, { title: "Acciones" }]}>
                      {rowsFlat.map(({ m, depth, hasKids }, i) => {
                        const unmatchedLeaf = !m.verified && m.status === "unmatched" && !hasKids;
                        const unmatchedIncome = unmatchedLeaf && m.direction === "credit" && INCOME_LIKE.has(m.category);
                        return (
                          <IndexTable.Row id={`m${m.id}`} key={m.id} position={i}>
                            <IndexTable.Cell>{formatDate(m.movement_date)}</IndexTable.Cell>
                            <IndexTable.Cell><Text variant="bodySm" tone="subdued">{accountName(m.account_id)}</Text></IndexTable.Cell>
                            <IndexTable.Cell>
                              <Box paddingInlineStart={depth ? "400" : "0"} maxWidth="380px">
                                {(() => {
                                  const who = m.client_name || m.payer_name;
                                  // Primary = who (or the concepto if we have no name); secondary = concepto
                                  // (the payment purpose) plus RFC/card so the movement is identifiable.
                                  const primary = who || m.concepto || "—";
                                  const sub = [who && m.concepto ? m.concepto : null,
                                    m.payer_rfc || (m.card_last4 ? `****${m.card_last4}` : null)]
                                    .filter(Boolean).join(" · ");
                                  return (<>
                                    <Text variant="bodySm" truncate>{depth ? "↳ " : ""}{primary}</Text>
                                    {sub && <Text variant="bodySm" tone="subdued" truncate>{sub}</Text>}
                                  </>);
                                })()}
                              </Box>
                            </IndexTable.Cell>
                            <IndexTable.Cell>{m.direction === "charge" ? formatPrice(m.amount) : ""}</IndexTable.Cell>
                            <IndexTable.Cell>{m.direction === "credit" ? formatPrice(m.amount) : ""}</IndexTable.Cell>
                            <IndexTable.Cell>
                              <Tooltip content="Cambiar categoría">
                                <div style={{ cursor: "pointer" }} onClick={() => openCat(m)}>
                                  <Badge size="small">{CATEGORY_LABEL[m.category] || m.category}</Badge>
                                </div>
                              </Tooltip>
                            </IndexTable.Cell>
                            <IndexTable.Cell>{statusBadge(m, hasKids)}</IndexTable.Cell>
                            <IndexTable.Cell>
                              <ButtonGroup>
                                <Tooltip content={m.verified ? "Quitar verificación" : "Verificar"}>
                                  <Button size="micro" pressed={m.verified} loading={busy === `v${m.id}`} onClick={() => toggleVerify(m)}>{m.verified ? "✓" : "Verificar"}</Button>
                                </Tooltip>
                                {unmatchedLeaf && <Button size="micro" variant="primary" loading={busy === `open${m.id}`} onClick={() => openMatch(m)}>Conciliar</Button>}
                                {unmatchedIncome && <Button size="micro" onClick={() => { setRegRow(m); setRegForm({ reservation: "" }); }}>Registrar</Button>}
                              </ButtonGroup>
                            </IndexTable.Cell>
                          </IndexTable.Row>
                        );
                      })}
                    </IndexTable>
                )}
              </BlockStack>
            </Card>
          )}
        </BlockStack>

        <Modal open={!!catRow} onClose={() => setCatRow(null)} title="Cambiar categoría"
          primaryAction={{ content: "Guardar", onAction: saveCat, loading: busy === "cat" }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setCatRow(null) }]}>
          <Modal.Section>
            {catRow && (
              <BlockStack gap="300">
                <Text tone="subdued" variant="bodySm">{catRow.payer_name || catRow.concepto || "—"} · {formatPrice(catRow.amount)} · {formatDate(catRow.movement_date)}</Text>
                <Select label="Categoría" value={catValue} onChange={setCatValue} options={allCategoryOptions} />
                <Text tone="subdued" variant="bodySm">Después de cambiarla, usa “Re-conciliar” para volver a aplicar las reglas con la nueva categoría.</Text>
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>

        <Modal open={!!matchRow} onClose={() => setMatchRow(null)} title="Conciliar con pago registrado" size="large"
          primaryAction={{ content: matchTied ? (matchSel.size > 1 ? `Conciliar ${matchSel.size} pagos` : "Conciliar") : "Selecciona pagos que cuadren", onAction: doMatchExisting, loading: busy === "matchsave", disabled: !matchTied }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setMatchRow(null) }]}>
          <Modal.Section>
            {matchRow && (
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" wrap>
                  <Text>Movimiento: <strong>{formatPrice(matchRow.amount)}</strong> · {formatDate(matchRow.movement_date)}{matchRow.payer_rfc ? ` · ${matchRow.payer_rfc}` : ""}</Text>
                  <InlineStack gap="200" blockAlign="center">
                    <Text tone="subdued" variant="bodySm">Seleccionado {formatPrice(matchSum)}</Text>
                    {matchTied
                      ? <Badge tone="success">Cuadra</Badge>
                      : <Badge tone={matchSel.size ? "warning" : undefined}>{`Faltan ${formatPrice(matchDiff)}`}</Badge>}
                  </InlineStack>
                </InlineStack>
                <Text tone="subdued" variant="bodySm">
                  Selecciona el o los pagos ya capturados en el sistema que suman este depósito. Un pago se enlaza directo; varios lo dividen en sub-movimientos conciliados.
                </Text>
                {matchCands.length === 0 ? (
                  <Box padding="400"><Text tone="subdued">Sin pagos registrados sin conciliar en ±21 días de esta fecha.</Text></Box>
                ) : (
                  <BlockStack gap="200">
                    <InlineStack gap="150" blockAlign="center" align="space-between">
                      <InlineStack gap="150" blockAlign="center">
                        <Text variant="bodySm" tone="subdued">Agrupar:</Text>
                        <ButtonGroup variant="segmented">
                          <Button size="slim" pressed={matchGroup === "none"} onClick={() => setMatchGroup("none")}>Ninguno</Button>
                          <Button size="slim" pressed={matchGroup === "reservation"} onClick={() => setMatchGroup("reservation")}>Reserva</Button>
                          <Button size="slim" pressed={matchGroup === "customer"} onClick={() => setMatchGroup("customer")}>Cliente</Button>
                        </ButtonGroup>
                      </InlineStack>
                      <Button size="slim" onClick={computeCombos}>Calcular combinaciones</Button>
                    </InlineStack>
                    {matchCombos && (
                      matchCombos.list.length === 0 ? (
                        <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                          <Text tone="subdued">Ninguna combinación encontrada que sume {formatPrice(matchRow.amount)}.</Text>
                        </Box>
                      ) : (
                        <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                          <BlockStack gap="200">
                            <Text variant="bodySm" fontWeight="semibold">
                              {matchCombos.list.length} combinación{matchCombos.list.length === 1 ? "" : "es"} que suman {formatPrice(matchRow.amount)}{matchCombos.capped ? " (primeras)" : ""}
                            </Text>
                            {matchCombos.list.map((combo, idx) => (
                              <InlineStack key={idx} align="space-between" blockAlign="center" wrap={false}>
                                <BlockStack gap="0">
                                  <Text variant="bodySm">{combo.custLabel} · {combo.items.length} pago{combo.items.length === 1 ? "" : "s"}</Text>
                                  <Text variant="bodySm" tone="subdued">{combo.items.map((i) => formatPrice(i.amount)).join(" + ")}</Text>
                                </BlockStack>
                                <Button size="slim" onClick={() => applyCombo(combo)}>Usar</Button>
                              </InlineStack>
                            ))}
                          </BlockStack>
                        </Box>
                      )
                    )}
                    <BlockStack gap="0">
                      {matchGroups().map((g) => {
                        const allSel = g.items.every((i) => matchSel.has(i.key));
                        const someSel = g.items.some((i) => matchSel.has(i.key));
                        const gSum = g.items.reduce((a, i) => a + i.amount, 0);
                        return (
                          <BlockStack key={g.key} gap="0">
                            {g.label && (
                              <Box background="bg-surface-secondary" padding="150" borderBlockEndWidth="025" borderColor="border">
                                <InlineStack align="space-between" blockAlign="center">
                                  <InlineStack gap="200" blockAlign="center">
                                    <Checkbox label="" labelHidden checked={allSel ? true : someSel ? "indeterminate" : false} onChange={() => toggleCandGroup(g.items)} />
                                    <Text variant="bodySm" fontWeight="semibold">{g.label}</Text>
                                    <Text variant="bodySm" tone="subdued">({g.items.length})</Text>
                                  </InlineStack>
                                  <Text variant="bodySm" tone="subdued">{formatPrice(gSum)}</Text>
                                </InlineStack>
                              </Box>
                            )}
                            {g.items.map(renderMatchCand)}
                          </BlockStack>
                        );
                      })}
                    </BlockStack>
                  </BlockStack>
                )}
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>

        <Modal open={!!regRow} onClose={() => setRegRow(null)} title="Registrar pago desde el banco"
          primaryAction={{ content: "Registrar", onAction: doRegister, loading: busy === "reg" }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setRegRow(null) }]}>
          <Modal.Section>
            {regRow && (
              <BlockStack gap="300">
                <Text>Monto: <strong>{formatPrice(regRow.amount)}</strong> · {formatDate(regRow.movement_date)}</Text>
                <Text tone="subdued" variant="bodySm">{regRow.concepto}{regRow.payer_rfc ? ` · ${regRow.payer_rfc}` : ""}</Text>
                <TextField label="Reservación (opcional)" type="number" value={regForm.reservation} onChange={(v) => setRegForm({ reservation: v })} autoComplete="off" helpText="Vacío = crédito del cliente sin aplicar." />
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

function ExCard({ tone, title, count, hint }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text variant="bodySm" tone="subdued">{title}</Text>
        <Text variant="headingLg" tone={count > 0 ? tone : "subdued"}>{count}</Text>
        <Text variant="bodySm" tone="subdued">{hint}</Text>
      </BlockStack>
    </Card>
  );
}

export default ConciliacionBancaria;
